import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Search,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatCard } from '@/components/StatCard';
import { StatusBadge } from '@/components/StatusBadge';
import { DataTablePagination } from '@/components/DataTablePagination';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { importsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import {
  IMPORT_SOURCE_LABEL,
  IMPORT_STATUS_LABEL,
  IMPORT_STATUS_VARIANT,
  fmtBytes,
  fmtDateTime,
  fmtDay,
  fmtInt,
  fmtMoney,
  fmtUsd,
} from '@/lib/format';
import { cn } from '@/lib/utils';
import { useCan } from '@/features/auth/store';

const PAGE_SIZE = 50;

export function ImportDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const canManage = useCan('imports.manage');
  const canDelete = useCan('imports.delete');
  const q = params.get('q') ?? '';
  const only = params.get('only') === 'sales' ? 'sales' : 'all';
  const page = Number(params.get('page') ?? 1);
  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => setSearchInput(q), [q]);
  const set = (k: string, v: string | undefined) => {
    const next = new URLSearchParams(params);
    if (!v) next.delete(k);
    else next.set(k, v);
    if (k !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };

  const imp = useQuery({
    queryKey: ['imports', id],
    queryFn: () => importsApi.get(id),
    enabled: !!id,
  });
  const rows = useQuery({
    queryKey: ['imports', id, 'rows', { q, only, page }],
    queryFn: () =>
      importsApi.rows(id, {
        q: q || undefined,
        only,
        page,
        pageSize: PAGE_SIZE,
        sortBy: 'grossSales',
        sortDir: 'desc',
      }),
    enabled: !!id,
    placeholderData: (p) => p,
  });
  const invalidate = () => {
    for (const key of ['imports', 'reports', 'dashboard', 'affiliates', 'products', 'months']) {
      void qc.invalidateQueries({ queryKey: [key] });
    }
  };
  const activate = useMutation({
    mutationFn: () => importsApi.activate(id),
    onSuccess: (r) => {
      toast.success(
        r.superseded.length
          ? `Ativada; ${r.superseded.length} importação(ões) substituída(s)`
          : 'Importação ativada',
      );
      invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro'),
  });
  const remove = useMutation({
    mutationFn: () => importsApi.remove(id),
    onSuccess: () => {
      toast.success('Importação excluída');
      invalidate();
      navigate('/importacoes');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro'),
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmActivate, setConfirmActivate] = useState(false);

  const i = imp.data;
  if (imp.isLoading || !i) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={`${fmtDay(i.periodStart)}${i.periodStart !== i.periodEnd ? ` – ${fmtDay(i.periodEnd)}` : ''}`}
        description={i.title ?? i.fileName}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() =>
                importsApi
                  .downloadFile(id, i.fileName)
                  .catch((e) => toast.error(e instanceof ApiError ? e.message : 'Erro'))
              }
            >
              <Download /> Arquivo original
            </Button>
            {canManage && i.status !== 'active' && (
              <Button onClick={() => setConfirmActivate(true)} disabled={activate.isPending}>
                {activate.isPending ? <Loader2 className="animate-spin" /> : <CheckCircle2 />}{' '}
                Ativar esta
              </Button>
            )}
            {canDelete && (
              <Button
                variant="outline"
                className="text-destructive"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 /> Excluir
              </Button>
            )}
            <Button variant="ghost" onClick={() => navigate('/importacoes')}>
              <ArrowLeft /> Voltar
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <StatusBadge
          label={IMPORT_STATUS_LABEL[i.status]}
          className={IMPORT_STATUS_VARIANT[i.status]}
        />
        <span className="text-muted-foreground">
          {IMPORT_SOURCE_LABEL[i.source]} · {i.createdByName ?? '—'} · {fmtDateTime(i.createdAt)}
        </span>
        <span className="flex items-center gap-1 text-muted-foreground">
          <FileSpreadsheet className="size-3.5" /> {i.fileName} ({fmtBytes(i.fileSize)})
        </span>
        {i.reportDate && (
          <span className="text-muted-foreground">
            · gerado na BuyGoods em {fmtDay(i.reportDate)}
          </span>
        )}
        {i.emailSubject && (
          <span className="text-muted-foreground">
            · e-mail: "{i.emailSubject}" de {i.emailFrom}
          </span>
        )}
      </div>

      {i.status === 'conflict' && (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="size-4" />
          <AlertTitle>Em conflito: não entra no faturamento</AlertTitle>
          <AlertDescription>
            O período cruza uma importação ativa de período diferente. "Ativar esta" substitui as
            que cruzam; "Excluir" descarta este arquivo.
          </AlertDescription>
        </Alert>
      )}
      {i.status === 'superseded' && (
        <Alert className="mb-4">
          <AlertTriangle className="size-4" />
          <AlertTitle>Substituída em {fmtDateTime(i.supersededAt)}</AlertTitle>
          <AlertDescription>
            Fica como histórico, fora das somas.{' '}
            {i.supersededById && (
              <Link className="underline" to={`/importacoes/${i.supersededById}`}>
                Ver a importação que a substituiu
              </Link>
            )}
          </AlertDescription>
        </Alert>
      )}
      {i.warnings.length > 0 && (
        <Alert className="mb-4">
          <AlertTriangle className="size-4 text-amber-600" />
          <AlertTitle>Avisos da leitura</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-4 text-xs">
              {i.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={FileSpreadsheet}
          label="Linhas"
          value={fmtInt(i.rowCount)}
          hint={`${fmtInt(i.affiliateCount)} afiliados · ${fmtInt(i.productCount)} produtos`}
        />
        <StatCard
          icon={FileSpreadsheet}
          label="Bruto"
          value={fmtUsd(i.totals.grossSales)}
          hint={`${fmtInt(i.totals.orders)} pedidos`}
        />
        <StatCard
          icon={FileSpreadsheet}
          label="Líquido"
          value={fmtUsd(i.totals.netSales)}
          hint={`comissões ${fmtUsd(i.totals.commissionsNet)}`}
          tone={i.totals.netSales < 0 ? 'destructive' : 'default'}
        />
        <StatCard
          icon={FileSpreadsheet}
          label="Visitantes"
          value={fmtInt(i.totals.visitors)}
          hint={`${fmtInt(i.totals.customers)} clientes`}
        />
      </div>

      <Card className="mt-6">
        <CardContent className="p-0">
          <div className="flex flex-wrap items-end gap-3 p-4">
            <div className="min-w-56 flex-1 space-y-1">
              <Label className="text-xs" htmlFor="rows-q">
                Buscar nas linhas
              </Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
                <Input
                  id="rows-q"
                  placeholder="Afiliado ou produto"
                  className="pl-8"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && set('q', searchInput.trim())}
                  onBlur={() => searchInput.trim() !== q && set('q', searchInput.trim())}
                />
              </div>
            </div>
            <div className="flex items-center gap-2 pb-2">
              <Switch
                id="rows-only"
                checked={only === 'sales'}
                onCheckedChange={(v) => set('only', v ? 'sales' : undefined)}
              />
              <Label htmlFor="rows-only" className="text-xs">
                Só com faturamento
              </Label>
            </div>
          </div>
          {rows.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 8 }).map((_, k) => (
                <Skeleton key={k} className="h-9" />
              ))}
            </div>
          ) : rows.data?.items.length ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12 text-right">Linha</TableHead>
                      <TableHead>Produto</TableHead>
                      <TableHead>Afiliado (como no arquivo)</TableHead>
                      <TableHead className="text-right">Visitantes</TableHead>
                      <TableHead className="text-right">Pedidos</TableHead>
                      <TableHead className="text-right">Bruto</TableHead>
                      <TableHead className="text-right">Comissões</TableHead>
                      <TableHead className="text-right">CB e reemb.</TableHead>
                      <TableHead className="text-right">Impostos</TableHead>
                      <TableHead className="text-right">Líquido</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.data.items.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                          {r.rowIndex}
                        </TableCell>
                        <TableCell>{r.rawAccount}</TableCell>
                        <TableCell>
                          <Link to={`/afiliados/${r.affiliate.id}`} className="hover:underline">
                            {r.rawAffiliate}
                          </Link>
                          {r.affiliate.name !== r.rawAffiliate.trim() && (
                            <span className="ml-1 text-xs text-muted-foreground">
                              → {r.affiliate.name}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtInt(r.visitors)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtInt(r.orders)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtMoney(r.grossSales)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtMoney(r.commissionsNet)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtMoney(r.totalCbsRefunds)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtMoney(r.taxes)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            'text-right font-medium tabular-nums',
                            r.netSales < 0 && 'text-destructive',
                          )}
                        >
                          {fmtMoney(r.netSales)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <DataTablePagination
                page={rows.data.page}
                pageSize={rows.data.pageSize}
                total={rows.data.total}
                onPageChange={(p) => set('page', String(p))}
              />
            </>
          ) : (
            <p className="p-6 text-sm text-muted-foreground">Nenhuma linha.</p>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta importação?</AlertDialogTitle>
            <AlertDialogDescription>
              As {fmtInt(i.rowCount)} linhas saem do sistema
              {i.status === 'active'
                ? ' e o período deixa de ter dados até você importar de novo'
                : ''}
              . O arquivo original também é apagado. A ação fica na auditoria.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => remove.mutate()}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmActivate} onOpenChange={setConfirmActivate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ativar esta importação?</AlertDialogTitle>
            <AlertDialogDescription>
              Ela passa a entrar no faturamento. Importações ativas cujo período cruza com este
              ficam como substituídas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmActivate(false);
                activate.mutate();
              }}
            >
              Ativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
